import { Telemetry, ApplicationInsights } from '@microsoft/applicationinsights-web';
import arch from 'arch';
import parser from 'ua-parser-js';
import startCase from 'lodash.startcase';

var WellKnownApplication;
(function (WellKnownApplication) {
    WellKnownApplication["AutomationHub"] = "UiPath.AutomationHub";
    WellKnownApplication["Orchestrator"] = "UiPath.Orchestrator";
    WellKnownApplication["AutomationStore"] = "UiPath.AutomationStore";
    WellKnownApplication["ProcessMining"] = "UiPath.ProcessMining";
    WellKnownApplication["AutomationOps"] = "UiPath.AutomationOps";
    WellKnownApplication["TaskCapture"] = "UiPath.TaskCapture";
    WellKnownApplication["Insights"] = "UiPath.Insights";
    WellKnownApplication["Solutions"] = "UiPath.Solutions";
    WellKnownApplication["PortalShell"] = "UiPath.PortalShell";
    WellKnownApplication["Portal"] = "UiPath.AutomationCloud";
})(WellKnownApplication || (WellKnownApplication = {}));

var WellKnownEvent;
(function (WellKnownEvent) {
    WellKnownEvent["Login"] = "Authenticate.LogIn";
    WellKnownEvent["Start"] = "Application.Start";
    WellKnownEvent["Navigate"] = "Application.Navigate";
    WellKnownEvent["Error"] = "Application.Error";
})(WellKnownEvent || (WellKnownEvent = {}));

var SeverityLevel;
(function (SeverityLevel) {
    SeverityLevel["Info"] = "Info";
    SeverityLevel["Normal"] = "Normal";
    SeverityLevel["Major"] = "Major";
    SeverityLevel["Critical"] = "Critical";
})(SeverityLevel || (SeverityLevel = {}));

var DeploymentType;
(function (DeploymentType) {
    /**
     * The application is deployed via MSI and hosted on-prem.
     */
    DeploymentType["Standalone"] = "Standalone";
    /**
     * The application is deployed onto a kubernetes cluster on-prem.
     */
    DeploymentType["AutomationSuite"] = "AutomationSuite";
    /**
     * The application is hosted in our Cloud.
     */
    DeploymentType["AutomationCloud"] = "AutomationCloud";
})(DeploymentType || (DeploymentType = {}));

const GLOBAL_LEVEL_TENANT = 'FD6B1BC4DDC3D84950BD767D4FF3BEC056C49770';

class ApplicationInsightDriver {
    constructor(instance, isInitialized = false) {
        this._instance = instance;
        this._isInitialized = isInitialized;
    }
    initialize() {
        if (this._isInitialized) {
            return;
        }
        this._instance.loadAppInsights();
        this._isInitialized = true;
    }
    track(name, properties) {
        this._instance
            .trackEvent({
            name,
            properties,
        });
    }
    trackException(error, severityLevel, properties) {
        this._instance
            .trackException({
            exception: error,
            severityLevel: appInsightsSeverityMap[severityLevel],
            properties,
        });
    }
    trackTrace(message, severityLevel, properties) {
        this._instance
            .trackTrace({
            message,
            severityLevel: appInsightsSeverityMap[severityLevel],
            properties,
        });
    }
    trackMetric(metricTrackingOptions, properties) {
        this._instance
            .trackMetric(Object.assign(Object.assign({}, metricTrackingOptions), { properties }));
    }
    trackPageView(pageViewTrackingOptions) {
        this._instance.context.telemetryTrace.traceID = Telemetry.Util.generateW3CId();
        this._instance.trackPageView(pageViewTrackingOptions);
    }
    trackPageViewPerformance(pageViewTrackingOptions) {
        this._instance.trackPageViewPerformance(pageViewTrackingOptions);
    }
    addTelemetryInitializer(telemetryInitializer) {
        this._instance.addTelemetryInitializer(telemetryInitializer);
    }
}
const appInsightsSeverityMap = {
    [SeverityLevel.Info]: 1,
    [SeverityLevel.Normal]: 2,
    [SeverityLevel.Major]: 3,
    [SeverityLevel.Critical]: 4,
};

class ApplicationInsightDriverBuilder {
    constructor() {
        this._isInitialized = false;
    }
    withConfig({ instrumentationKey, disableFetchTracking, disableAjaxTracking, }) {
        const isTelemetryDisabled = instrumentationKey.trim() === '';
        this._instance = new ApplicationInsights({
            config: {
                instrumentationKey,
                disableTelemetry: isTelemetryDisabled,
                disableFetchTracking: !!disableFetchTracking,
                disableAjaxTracking: !!disableAjaxTracking,
                disableCookiesUsage: true,
            },
        });
        return this;
    }
    withInstance(instance, isInitialized = false) {
        this._instance = instance;
        this._isInitialized = isInitialized;
        return this;
    }
    build() {
        if (this._instance) {
            return new ApplicationInsightDriver(this._instance, this._isInitialized);
        }
        throw new Error(`Not able to build AppInsights Driver.`);
    }
}
const AppInsightDriverBuilder = new ApplicationInsightDriverBuilder();

const browserInfo = () => {
    const touchCapable = 'ontouchstart' in window
        || window.DocumentTouch && document instanceof window.DocumentTouch
        || navigator.maxTouchPoints > 0
        || window.navigator.msMaxTouchPoints > 0;
    return {
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        orientation: window.screen.orientation.type.includes('landscape')
            ? 'landscape'
            : 'portrait',
        touchCapable,
    };
};

const DEVICE_TYPE = 'browser';
const sessionInfo = (store) => {
    var _a;
    const parsed = parser(navigator.userAgent);
    return {
        browserVersion: `${parsed.browser.name} ${parsed.browser.version}`,
        oSVersion: `${parsed.os.name} ${parsed.os.version}`,
        oSArchitecture: arch(),
        devicePixelRatio: window.devicePixelRatio,
        productLanguage: store.language,
        systemLanguage: navigator.language,
        deviceType: (_a = parsed.device.type) !== null && _a !== void 0 ? _a : DEVICE_TYPE,
    };
};

const platformInfo = (store, deploymentType) => {
    var _a, _b;
    switch (deploymentType) {
        case DeploymentType.Standalone:
            return {
                localUserId: store.localUserId,
                tenantId: store.tenantId,
                sessionId: store.sessionId,
                installationId: store.installationId,
                productVersion: store.productVersion,
                isHost: (_a = store.isHost) !== null && _a !== void 0 ? _a : false,
            };
        case DeploymentType.AutomationSuite:
            return {
                cloudUserId: store.cloudUserId,
                cloudOrganizationId: store.cloudOrganizationId,
                cloudTenantId: store.cloudTenantId,
                sessionId: store.sessionId,
                clusterId: store.clusterId,
                productVersion: store.productVersion,
                isHost: (_b = store.isHost) !== null && _b !== void 0 ? _b : false,
            };
        default:
            return {
                cloudUserId: store.cloudUserId,
                cloudOrganizationId: store.cloudOrganizationId,
                cloudTenantId: store.cloudTenantId,
                sessionId: store.sessionId,
                productVersion: store.productVersion,
            };
    }
};

const assertDefined = (key, value) => {
    if (value == null) {
        throw new Error(`Property ${key} is not defined.`);
    }
    return value;
};

const CIRCULAR = Symbol('circular');
const markCircularReferences = (val, refs) => {
    refs = refs !== null && refs !== void 0 ? refs : new WeakSet();
    if (!val || typeof val !== 'object' || Object.keys(val).length === 0) {
        return val;
    }
    if (refs.has(val)) {
        return CIRCULAR;
    }
    refs.add(val);
    const obj = (Array.isArray(val) ? [] : {});
    for (const idx in val) {
        obj[idx] = markCircularReferences(val[idx], refs);
    }
    refs.delete(val);
    return obj;
};

const pascalCase = (key) => startCase(key).replace(/ /g, '');

const pascalify = (obj, shouldMark = true) => {
    if (shouldMark) {
        obj = markCircularReferences(obj);
    }
    const result = {};
    if (!obj || typeof obj !== 'object') {
        return obj;
    }
    for (const key in obj) {
        const value = obj[key];
        if (Array.isArray(value)) {
            result[pascalCase(key)] = value.filter(item => item !== CIRCULAR).map(item => pascalify(item, false));
        }
        else if (typeof value === 'object' && Object.keys(value).length > 0) {
            result[pascalCase(key)] = pascalify(value, false);
        }
        else if (value !== CIRCULAR) {
            result[pascalCase(key)] = value;
        }
    }
    return result;
};

const eventToInvocationSource = (event) => {
    const ev = assertDefined('event', event);
    const detail = assertDefined('event.detail', ev.detail);
    return detail === 0 ? 'keyboard' : 'mouse';
};

const coerceInvocationSource = (source) => typeof source === 'string'
    ? source
    : eventToInvocationSource(source);

/**
 * TODO: Standalone
 * Figure out how to handle LocalUserId / LocalSessionId / CloudUserId
 */
const TelemetryDataKey = {
    language: {
        key: `language`,
        requiredOnDeployment: [
            DeploymentType.AutomationCloud,
            DeploymentType.AutomationSuite,
            DeploymentType.Standalone,
        ],
    },
    cloudUserId: {
        key: `cloudUserId`,
        requiredOnDeployment: [
            DeploymentType.AutomationCloud,
            DeploymentType.AutomationSuite,
        ],
    },
    cloudOrganizationId: {
        key: `cloudOrganizationId`,
        requiredOnDeployment: [
            DeploymentType.AutomationCloud,
            DeploymentType.AutomationSuite,
        ],
    },
    cloudTenantId: {
        key: `cloudTenantId`,
        requiredOnDeployment: [
            DeploymentType.AutomationCloud,
            DeploymentType.AutomationSuite,
        ],
    },
    sessionId: {
        key: `sessionId`,
        requiredOnDeployment: [DeploymentType.AutomationCloud],
    },
    productVersion: {
        key: `productVersion`,
        requiredOnDeployment: [
            DeploymentType.AutomationSuite,
            DeploymentType.Standalone,
        ],
    },
    installationId: {
        key: `installationId`,
        requiredOnDeployment: [DeploymentType.Standalone],
    },
    clusterId: {
        key: `clusterId`,
        requiredOnDeployment: [DeploymentType.AutomationSuite],
    },
    localUserId: {
        key: `localUserId`,
        requiredOnDeployment: [DeploymentType.Standalone],
    },
    tenantId: {
        key: `tenantId`,
        requiredOnDeployment: [DeploymentType.Standalone],
    },
};
const ALL_STORE_PROPERTIES_NAMES = Object.freeze(Object.keys(TelemetryDataKey));
Object.freeze(ALL_STORE_PROPERTIES_NAMES.filter(property => TelemetryDataKey[property].requiredOnDeployment
    .includes(DeploymentType.AutomationCloud)));
Object.freeze(ALL_STORE_PROPERTIES_NAMES.filter(property => TelemetryDataKey[property].requiredOnDeployment
    .includes(DeploymentType.AutomationSuite)));
Object.freeze(ALL_STORE_PROPERTIES_NAMES.filter(property => TelemetryDataKey[property].requiredOnDeployment
    .includes(DeploymentType.Standalone)));
class TelemetryStorage {
    constructor(deploymentType) {
        this._store = new Map();
        const properties = Object.keys(TelemetryDataKey);
        properties.forEach(property => {
            const { key, requiredOnDeployment: requiredOnEnvironment, } = TelemetryDataKey[property];
            Object.defineProperty(this, property, {
                set: (value) => this._store.set(key, value),
                get: () => {
                    if (!requiredOnEnvironment.includes(deploymentType)) {
                        return this._store.get(key);
                    }
                    return this._safeGet(key);
                },
            });
        });
    }
    clear() {
        this._store.clear();
    }
    _safeGet(key) {
        return assertDefined(key, this._store.get(key));
    }
}

function objectPropertyExtractor(o) {
    const extractor = function ({ object, references, }) {
        const properties = {};
        const keys = Object.keys(object);
        keys.forEach(key => {
            const isOwnProperty = Object.prototype.hasOwnProperty.call(object, key);
            if (!isOwnProperty) {
                return;
            }
            const value = object[key];
            if (value == null) {
                return;
            }
            if (typeof value === 'string' || typeof value === 'number') {
                properties[key] = value;
            }
            if (typeof value === 'object' && !references.has(value)) {
                references.add(value);
                const nestedProperties = extractor({
                    object: value,
                    references,
                });
                Object
                    .keys(nestedProperties)
                    .forEach(nestedKey => {
                    properties[`${key}_${nestedKey}`] = nestedProperties[nestedKey];
                });
            }
        });
        return properties;
    };
    return extractor({
        object: o,
        references: new WeakSet([o]),
    });
}

/**
 * The Tracker class is responsible for tracking user events and sending them to the telemetry server.
 * @export
 * @class Tracker
 */
class Tracker {
    get _properties() {
        return Object.assign(Object.assign(Object.assign({ applicationName: this._applicationName, currentRoute: window.location.pathname }, browserInfo()), platformInfo(this._store, this._deploymentType)), { deploymentType: this._deploymentType });
    }
    /**
     *
     * @param deploymentType The deployment type of the application.
     * @param _applicationName - The application name is used to identify the application in the telemetry server.
     * @param _driver - The driver used to send the telemetry data to the telemetry server.
     */
    constructor(_deploymentType, _applicationName, _driver) {
        this._deploymentType = _deploymentType;
        this._applicationName = _applicationName;
        this._driver = _driver;
        this._queue = [];
        this._isInitialized = false;
        this._store = new TelemetryStorage(_deploymentType);
    }
    /**
     * This method is used to initialize the telemetry client.
     * @param isDevelopment - specify to get more context on potential errors
     * @param isPageViewTrackingEnabled - on by default for AutomationCloud
     * @param isPageViewPerformanceTrackingEnabled - off by default
     * @param pageViewTrackingOptions - if passed, the options are added while tracking page view
     * @param initializer - use to mutate or filter out events
     */
    initialize({ isDevelopment = false, isPageViewTrackingEnabled = this._deploymentType === DeploymentType.AutomationCloud, isPageViewPerformanceTrackingEnabled = false, pageViewTrackingOptions, language, initializer, }) {
        this._store.isDevelopment = isDevelopment;
        this._store.isPageViewTrackingEnabled = isPageViewTrackingEnabled;
        this._store.isPageViewPerformanceTrackingEnabled = isPageViewPerformanceTrackingEnabled;
        this._store.pageViewTrackingOptions = pageViewTrackingOptions;
        this._driver.initialize();
        this.setLanguage(language);
        if (initializer) {
            this._driver.addTelemetryInitializer(initializer);
        }
        this._isInitialized = true;
        this._dequeue();
    }
    /**
     * This will store the application language in the telemetry store.
     * @param language The language of the application.
     */
    setLanguage(language) {
        this._store.language = language;
    }
    /**
     * This will store the platform information in the telemetry store.
     * @param info - The platform information to send to the telemetry server.
     */
    setPlatformInfo(info) {
        const infoTyped = info;
        Object.keys(info)
            .forEach((key) => {
            const value = infoTyped[key];
            if (value == null) {
                return;
            }
            // FIXME: ugly cast because of https://github.com/microsoft/TypeScript/issues/38735
            this._store[key] = value;
        });
    }
    /**
     * This method is used to track a user event.
     * All of the common properties are extracted automatically and attached to the provided details.
     * @param event - The event to track.
     * @param details - The details of the event.
     */
    trackEvent(event, ...[details]) {
        try {
            const invocationSource = details === null || details === void 0 ? void 0 : details.invocationSource;
            const invocationSourcePatch = invocationSource ?
                { invocationSource: coerceInvocationSource(invocationSource) }
                : {};
            const navigationTargetRoutePatch = event === WellKnownEvent.Navigate && details.targetRoute
                ? { targetRoute: details.targetRoute }
                : {};
            const navigationCurrentRoutePatch = event === WellKnownEvent.Navigate && details.currentRoute
                ? { currentRoute: details.currentRoute }
                : {};
            const getProperties = () => pascalify(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, this._properties), details), invocationSourcePatch), navigationTargetRoutePatch), navigationCurrentRoutePatch));
            this._enqueue(() => {
                this._driver.track(event.toString(), getProperties());
            });
        }
        catch (error) {
            if (!this._store.isDevelopment) {
                return;
            }
            // eslint-disable-next-line no-console
            console.error('Telemetry event failed was not sent:', {
                event,
                details,
                error,
            });
        }
    }
    /**
     * This method is used to track a navigate event.
     * @param details - The details of the navigate event.
     */
    trackNavigation(details) {
        var _a;
        this.trackEvent(WellKnownEvent.Navigate, Object.assign({}, details));
        if (this._store.isPageViewTrackingEnabled) {
            this._driver.trackPageView(this._store.pageViewTrackingOptions);
        }
        if (this._store.isPageViewPerformanceTrackingEnabled) {
            this._driver.trackPageViewPerformance((_a = this._store.pageViewTrackingOptions) !== null && _a !== void 0 ? _a : {});
        }
    }
    /**
     * This method is used to track a login event.
     * @param details - The details of the login event.
     */
    trackLogin(details) {
        this.trackEvent(WellKnownEvent.Login, Object.assign({}, details));
    }
    /**
     * This method is used to track when a new application load (bootstrap) occurs.
     * @param details - The details of the app start event.
     */
    trackApplicationStart(details = {}) {
        this._enqueue(() => this.trackEvent(WellKnownEvent.Start, Object.assign(Object.assign({}, details), sessionInfo(this._store))));
    }
    /**
     * This method is used to track when an application application error is thrown.
     * @param error - The error object thrown.
     * @param severityLevel - How severe the error is, how big of an impact it has on the application. Defaults to `Normal`
     */
    trackError(error, severityLevel = SeverityLevel.Normal, details = {}) {
        if (error instanceof Error) {
            try {
                const properties = pascalify(Object.assign(Object.assign(Object.assign({}, this._properties), details), objectPropertyExtractor(error)));
                this._enqueue(() => {
                    this._driver.trackException(error, severityLevel, properties);
                });
            }
            catch (e) {
                if (this._store.isDevelopment) {
                    // eslint-disable-next-line no-console
                    console.error('trackError failed', {
                        exceptionToTrack: error,
                        severityLevel,
                        error: e,
                        details,
                    });
                }
            }
            return;
        }
        if (this._store.isDevelopment) {
            // eslint-disable-next-line no-console
            console.error('trackError was called with a bad parameter', { error });
        }
    }
    /**
     * This method is used to track trace message.
     * @param message - The trace message.
     * @param severityLevel - How severe the error is, how big of an impact it has on the application. Defaults to `Normal`
     */
    trackTrace(message, severityLevel = SeverityLevel.Normal, details) {
        try {
            const properties = pascalify(Object.assign(Object.assign({}, this._properties), details));
            this._enqueue(() => {
                this._driver.trackTrace(message, severityLevel, properties);
            });
        }
        catch (e) {
            if (this._store.isDevelopment) {
                // eslint-disable-next-line no-console
                console.error('trackTrace failed', {
                    message,
                    severityLevel,
                    error: e,
                    details,
                });
            }
        }
    }
    /**
     * This method is used to track a metric.
     * @param metric - The metric object.
     */
    trackMetric(metric, details) {
        try {
            const properties = pascalify(Object.assign(Object.assign({}, this._properties), details));
            this._enqueue(() => {
                this._driver.trackMetric(metric, properties);
            });
        }
        catch (e) {
            if (this._store.isDevelopment) {
                // eslint-disable-next-line no-console
                console.error('trackMetric failed', {
                    metric,
                    error: e,
                    details,
                });
            }
        }
    }
    _dequeue() {
        this._queue.forEach(trackingCallback => trackingCallback());
    }
    _enqueue(cb) {
        if (this._isInitialized) {
            cb();
        }
        else {
            this._queue.push(cb);
        }
    }
}

export { AppInsightDriverBuilder, DeploymentType, GLOBAL_LEVEL_TENANT, SeverityLevel, Tracker, WellKnownApplication, WellKnownEvent, eventToInvocationSource };
